import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
} from "antd";
import {
  CheckCircleOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  FileWordOutlined,
  MailOutlined,
  PhoneOutlined,
  RightOutlined,
  SearchOutlined,
  StopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import apiClient from "../../actions/api";
import { loadUser } from "../../actions/auth";
import { loadPagePermissions } from "../../actions/permissions";
import { getRolePermissions, PAGE_LABELS } from "../../config/pagePermissions";
import {
  COUNTRY_OPTIONS,
  getCountryName,
  normalizeJobTypeCountry,
} from "../../config/countries";
import isEmpty from "../../validations/is-empty";
import openNotification from "../../components/Notifications/openNotification";
import LogoSrc from "../../assets/image/logo.svg";
import "../../style/user/manageProfiles.scss";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const { TextArea } = Input;

const roles = {
  1: { label: "Admin", color: "red" },
  2: { label: "User", color: "blue" },
  3: { label: "Bidder", color: "purple" },
  4: { label: "Guest", color: "default" },
};

const ManageProfiles = ({ title }) => {
  document.title = title;
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const permissionState = useSelector((state) => state.permissions);
  const [form] = Form.useForm();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissionMode, setPermissionMode] = useState("role");
  const [customPagePermissions, setCustomPagePermissions] = useState([]);
  const [permissionsTouched, setPermissionsTouched] = useState(false);
  const [search, setSearch] = useState("");
  const [allowedFilter, setAllowedFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/users/get-users");
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      openNotification(
        "error",
        "Unable to load profiles",
        error?.response?.data?.message || "Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (!permissionState.loading || permissionState.roles.length) return;
    dispatch(loadPagePermissions()).catch(() => {});
  }, [dispatch, permissionState.loading, permissionState.roles.length]);

  const selectedRole = Form.useWatch("role", form);

  const roleDefaultPermissions = useMemo(
    () => getRolePermissions(permissionState.roles, selectedRole),
    [permissionState.roles, selectedRole],
  );

  const visiblePagePermissions =
    permissionMode === "custom"
      ? customPagePermissions
      : roleDefaultPermissions;

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter((user) => {
      const matchesSearch =
        !query ||
        `${user.name || ""} ${user.email || ""} ${roles[user.role]?.label || ""}`
          .toLowerCase()
          .includes(query);
      const matchesAllowed =
        allowedFilter === "all" ||
        (allowedFilter === "allowed" ? Boolean(user.allowed) : !user.allowed);

      return matchesSearch && matchesAllowed;
    });
  }, [allowedFilter, search, users]);

  const profileCompletion = (user) => {
    const fields = [
      user.name,
      user.email,
      user.phone,
      user.gender,
      user.job_type,
      user.avatar,
      user.email_verified,
    ];
    return Math.round(
      (fields.filter(
        (value) =>
          value !== undefined &&
          value !== null &&
          value !== "" &&
          value !== false,
      ).length /
        fields.length) *
        100,
    );
  };

  const openEditor = (user) => {
    const userHasCustomPermissions = Array.isArray(user.page_permissions);
    setSelectedUser(user);
    setPermissionMode(userHasCustomPermissions ? "custom" : "role");
    setCustomPagePermissions(
      userHasCustomPermissions ? [...user.page_permissions] : [],
    );
    setPermissionsTouched(false);
    form.setFieldsValue({
      name: user.name,
      email: user.email,
      role: user.role,
      gender: user.gender ?? "",
      phone: user.phone,
      job_type: normalizeJobTypeCountry(user.job_type),
      text_color: user.text_color,
      background_color: user.background_color,
      personal_prompt: user.personal_prompt,
      call_prompt: user.call_prompt,
      allowed: user.allowed ? "allowed" : "blocked",
      email_verified: user.email_verified ? "verified" : "unverified",
      balance: Number(user.balance || 0),
    });
  };

  const changePermissionMode = (mode) => {
    setPermissionsTouched(true);
    setPermissionMode(mode);
    if (mode === "custom" && customPagePermissions.length === 0) {
      setCustomPagePermissions([...roleDefaultPermissions]);
    }
  };

  const togglePagePermission = (pageKey, checked) => {
    setPermissionsTouched(true);
    setCustomPagePermissions((current) =>
      checked
        ? [...new Set([...current, pageKey])]
        : current.filter((permission) => permission !== pageKey),
    );
  };

  const downloadTemplate = (filename, label) => {
    if (isEmpty(filename)) return;
    const link = document.createElement("a");
    link.href = `${API_BASE_URL}/template/${filename}`;
    link.download = `${selectedUser?.name || "profile"}-${label}.docx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const saveProfile = async (values) => {
    setSaving(true);

    try {
      const profileUpdate = {
        id: selectedUser._id,
        name: values.name,
        email: values.email,
        role: values.role,
        gender: values.gender,
        phone: values.phone,
        job_type: normalizeJobTypeCountry(values.job_type),
        text_color: values.text_color,
        background_color: values.background_color,
        allowed: values.allowed === "allowed",
        email_verified: values.email_verified === "verified",
        balance: values.balance,
      };
      if (permissionsTouched) {
        profileUpdate.page_permissions =
          permissionMode === "custom" ? customPagePermissions : null;
      }
      const response = await apiClient.post(
        "/users/manage-profile",
        profileUpdate,
      );
      if (response.data?.status && response.data.status !== "success") {
        throw new Error("The profile could not be updated.");
      }
      openNotification(
        "success",
        "Profile updated",
        `${values.name}'s profile was saved.`,
      );
      setSelectedUser(null);
      await loadUsers();
      if (selectedUser._id === authUser?._id) {
        await dispatch(loadUser());
      }
    } catch (error) {
      const errors = error?.response?.data?.errors;
      if (Array.isArray(errors)) {
        errors.forEach((item) =>
          openNotification("error", "Validation Error", item.msg),
        );
      } else {
        openNotification(
          "error",
          "Update failed",
          error.message || "Please try again.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="manageProfilesSection">
      <div className="manage-profiles-heading">
        <div>
          <h2 className="title">
            <span className="manage-profiles-title-icon">
              <TeamOutlined />
            </span>
            Manage Profiles
          </h2>
          <p>View and update user details, permissions, and preferences.</p>
        </div>
        <Tag className="profile-count">{users.length} profiles</Tag>
      </div>

      <Card className="manage-profiles-toolbar" variant="borderless">
        <div className="manage-profiles-filters">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search name, email, or role"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select
            aria-label="Filter profiles by allowed status"
            value={allowedFilter}
            onChange={setAllowedFilter}
            options={[
              { value: "all", label: "All statuses" },
              { value: "allowed", label: "Allowed" },
              { value: "blocked", label: "Blocked" },
            ]}
          />
        </div>
      </Card>

      {loading ? (
        <div className="profiles-state">
          <Spin size="large" />
        </div>
      ) : isEmpty(filteredUsers) ? (
        <div className="profiles-state">
          <Empty description="No profiles found" />
        </div>
      ) : (
        <div className="profiles-grid">
          {filteredUsers.map((user) => (
            <Card
              key={user._id || user.email}
              className="profile-card"
              variant="borderless"
              role="button"
              tabIndex={0}
              aria-label={`Open ${user.name}'s profile`}
              onClick={() => openEditor(user)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openEditor(user);
                }
              }}
            >
              <div className="profile-card-header">
                <Avatar size={58} src={user.avatar || LogoSrc} />
                <div className="profile-card-copy">
                  <strong>{user.name}</strong>
                  <Tag
                    color={roles[user.role]?.color}
                    className="profile-role-tag"
                  >
                    {roles[user.role]?.label || "Unknown role"}
                  </Tag>
                </div>
              </div>

              <div className="profile-completion">
                <div>
                  <span>Profile data completion</span>
                  <strong>{profileCompletion(user)}%</strong>
                </div>
                <Progress
                  percent={profileCompletion(user)}
                  showInfo={false}
                  strokeColor={{ from: "#7c4dff", to: "#256bfe" }}
                />
              </div>

              <div className="profile-contact-list">
                <div>
                  <MailOutlined />
                  <span>{user.email || "Email not provided"}</span>
                </div>
                <div>
                  <PhoneOutlined />
                  <span>{user.phone || "Phone not provided"}</span>
                </div>
                <div>
                  <EnvironmentOutlined />
                  <span>
                    {getCountryName(user.job_type) || "Country not provided"}
                  </span>
                </div>
              </div>

              <div className="profile-card-tags">
                <Tag color="cyan">
                  Balance ${Number(user.balance || 0).toFixed(3)}
                </Tag>
                <Tag color={user.allowed ? "green" : "red"}>
                  {user.allowed ? "Allowed" : "Blocked"}
                </Tag>
                <Tag color={user.email_verified ? "cyan" : "orange"}>
                  {user.email_verified ? "Verified" : "Unverified"}
                </Tag>
                <Tag
                  color={
                    Array.isArray(user.page_permissions) ? "gold" : "geekblue"
                  }
                >
                  {Array.isArray(user.page_permissions)
                    ? "Custom access"
                    : "Role access"}
                </Tag>
              </div>

              <div className="profile-card-open">
                <span>Open profile</span>
                <RightOutlined />
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        rootClassName="manage-profile-modal"
        title="Manage profile"
        open={Boolean(selectedUser)}
        onCancel={() => setSelectedUser(null)}
        footer={null}
        width={980}
        destroyOnHidden
      >
        {selectedUser && (
          <>
            <div className="managed-profile-summary">
              <Avatar size={76} src={selectedUser.avatar || LogoSrc} />
              <div>
                <strong>{selectedUser.name}</strong>
                <span>Avatar and templates are view-only</span>
              </div>
              <Space wrap>
                <Button
                  icon={<FileWordOutlined />}
                  disabled={isEmpty(selectedUser.template_url)}
                  onClick={() =>
                    downloadTemplate(selectedUser.template_url, "resume")
                  }
                >
                  Download resume
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  disabled={isEmpty(selectedUser.template_coverletter_url)}
                  onClick={() =>
                    downloadTemplate(
                      selectedUser.template_coverletter_url,
                      "cover-letter",
                    )
                  }
                >
                  Download cover letter
                </Button>
              </Space>
            </div>
            <Form form={form} layout="vertical" onFinish={saveProfile}>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="User Name"
                    name="name"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[{ required: true, type: "email" }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Phone Number" name="phone">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Gender" name="gender">
                    <Select
                      options={[
                        { value: "", label: "Not specified" },
                        { value: "0", label: "Male" },
                        { value: "1", label: "Female" },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Country"
                    name="job_type"
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      placeholder="Select a country"
                      optionFilterProp="label"
                      options={COUNTRY_OPTIONS}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Role"
                    name="role"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={Object.entries(roles).map(([value, role]) => ({
                        value: Number(value),
                        label: role.label,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Account Balance"
                    name="balance"
                    rules={[{ required: true, type: "number", min: 0 }]}
                  >
                    <InputNumber
                      min={0}
                      precision={6}
                      prefix="$"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Background Color" name="background_color">
                    <Input type="color" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Text Color" name="text_color">
                    <Input type="color" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Allowed"
                    name="allowed"
                    rules={[{ required: true }]}
                  >
                    <Segmented
                      block
                      className="allowed-segmented"
                      options={[
                        {
                          value: "allowed",
                          icon: <CheckCircleOutlined />,
                          label: "Allowed",
                        },
                        {
                          value: "blocked",
                          icon: <StopOutlined />,
                          label: "Blocked",
                        },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Email Verified"
                    name="email_verified"
                    rules={[{ required: true }]}
                  >
                    <Segmented
                      block
                      className="allowed-segmented"
                      options={[
                        {
                          value: "verified",
                          icon: <CheckCircleOutlined />,
                          label: "Verified",
                        },
                        {
                          value: "unverified",
                          icon: <StopOutlined />,
                          label: "Unverified",
                        },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Divider orientation="left">Page Access</Divider>
                  <div className="managed-profile-permissions">
                    <div className="managed-profile-permissions-bar">
                      <Segmented
                        value={permissionMode}
                        onChange={changePermissionMode}
                        options={[
                          { value: "role", label: "Role default" },
                          { value: "custom", label: "Custom" },
                        ]}
                      />
                      <Tag
                        color={
                          permissionMode === "custom" ? "gold" : "geekblue"
                        }
                      >
                        {visiblePagePermissions.length} pages
                      </Tag>
                    </div>
                    <div
                      className={
                        permissionMode === "custom"
                          ? "managed-profile-page-grid"
                          : "managed-profile-page-grid is-readonly"
                      }
                    >
                      {permissionState.pageKeys.map((pageKey) => (
                        <Checkbox
                          key={pageKey}
                          checked={visiblePagePermissions.includes(pageKey)}
                          disabled={permissionMode !== "custom"}
                          onChange={(event) =>
                            togglePagePermission(pageKey, event.target.checked)
                          }
                        >
                          {PAGE_LABELS[pageKey] || pageKey}
                        </Checkbox>
                      ))}
                    </div>
                  </div>
                </Col>
                <Col span={24}>
                  <Form.Item label="Bid Prompt" name="personal_prompt">
                    <TextArea
                      className="manage-profile-readonly"
                      rows={10}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Call Prompt" name="call_prompt">
                    <TextArea
                      className="manage-profile-readonly"
                      rows={10}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
              <div className="managed-profile-actions">
                <Button onClick={() => setSelectedUser(null)}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={saving}>
                  Update Profile
                </Button>
              </div>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ManageProfiles;
